import { EVENT_TYPE_NAMES } from '../../common/analytics/amplitudeEventTypesUtil';
import { getAmplitudeEventData } from '../../common/analytics/constructAmplitudeEvent';
import { AMPLITUDE_USER_PROPS } from '../../common/analytics/statsConstants';
import { ELEMENT_CREATE } from '../../common/elements/elementConstants';
import { ElementType } from '../../common/elements/elementTypes';
import { removeUserAnalyticsTracker, updateUserAnalyticsTracker } from '../../common/users/userAnalyticsActions';
import { MNUserAnalytics, PendingEvent, UserAnalyticsTrackerType } from '../../common/users/userAnalyticsTypes';
import { sendAmplitudeEvent } from './amplitudeService';
import { BATCH_ACTION_TYPE } from '../../common/store/reduxBulkingMiddleware';
import { TEMPLATE_PICKER_TEMPLATE_APPLIED } from '../workspace/boardTemplatePickerPopup/boardTemplateActions';
import { ReduxStore } from '../types/reduxTypes';
import { hasOneTrustTargetingConsent } from '../app/oneTrust/oneTrustUtils';
import { getUserAnalytics } from '../user/userAnalyticsSelector';
import type { GenericReduxThunkAction } from '../../common/actions/actionTypes';

type CreateAction = {
    creationSource?: string;
    duplicate?: boolean;
    elementType?: string;
    isRedo?: boolean;
    isUndo?: boolean;
    location?: object;
    payload?: CreateAction[];
    remote?: boolean;
    type?: string;
};

type LogicalCreateEvent = {
    containsImage: boolean;
    imageCreateAction?: CreateAction;
    representativeCreateAction: CreateAction;
};

type ElementCreateMilestoneTrackingUpdate = {
    completedTrackers: Array<{
        trackerType: UserAnalyticsTrackerType;
        pendingEvent: PendingEvent;
    }>;
    updatedTracker?: {
        trackerType: UserAnalyticsTrackerType;
        state: {
            elementCreationCount: number;
        };
    };
};

const EVENT_TYPE_BY_TRACKER_TYPE: Record<UserAnalyticsTrackerType, string> = {
    [UserAnalyticsTrackerType.FIRST_ELEMENT_CREATION]: EVENT_TYPE_NAMES.ELEMENT_CREATION_FIRST,
    [UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION]: EVENT_TYPE_NAMES.ELEMENT_CREATION_THIRD,
    [UserAnalyticsTrackerType.FIRST_IMAGE_CREATION]: EVENT_TYPE_NAMES.ELEMENT_CREATION_FIRST_IMAGE,
};

const ELEMENT_CREATE_MILESTONE_TRACKER_TYPES = Object.keys(EVENT_TYPE_BY_TRACKER_TYPE) as UserAnalyticsTrackerType[];

const isPendingTracker = (tracker?: MNUserAnalytics[UserAnalyticsTrackerType]): boolean =>
    !!tracker && !tracker.pendingEvent;

export const shouldSendCompletedMilestonesForAnalyticsChange = (
    previousUserAnalytics?: MNUserAnalytics,
    userAnalytics?: MNUserAnalytics,
): boolean => {
    const hasCompletedMilestones = ELEMENT_CREATE_MILESTONE_TRACKER_TYPES.some(
        (trackerType) => userAnalytics?.[trackerType]?.pendingEvent,
    );
    if (!hasCompletedMilestones) return false;

    return !ELEMENT_CREATE_MILESTONE_TRACKER_TYPES.some(
        (trackerType) => previousUserAnalytics?.[trackerType]?.pendingEvent,
    );
};

const getLogicalCreateEvent = (action?: CreateAction): LogicalCreateEvent | null => {
    if (!action || action.remote || action.isUndo || action.isRedo || action.duplicate) return null;

    if (action.type === TEMPLATE_PICKER_TEMPLATE_APPLIED) {
        return {
            containsImage: false,
            representativeCreateAction: {
                type: ELEMENT_CREATE,
                creationSource: action.creationSource,
                elementType: action.elementType,
                location: action.location,
            },
        };
    }

    if (action.type === ELEMENT_CREATE) {
        const containsImage = action.elementType === ElementType.IMAGE_TYPE;

        return {
            containsImage,
            imageCreateAction: containsImage ? action : undefined,
            representativeCreateAction: action,
        };
    }

    if (action.type !== BATCH_ACTION_TYPE || !action.payload?.length) return null;

    // Element creation batches represent one user operation, such as duplicating multiple elements.
    return action.payload.reduce<LogicalCreateEvent | null>((logicalCreateEvent, payloadAction) => {
        const nestedLogicalCreateEvent = getLogicalCreateEvent(payloadAction);
        if (!nestedLogicalCreateEvent) return logicalCreateEvent;

        if (!logicalCreateEvent) return nestedLogicalCreateEvent;

        return {
            containsImage: logicalCreateEvent.containsImage || nestedLogicalCreateEvent.containsImage,
            imageCreateAction: logicalCreateEvent.imageCreateAction || nestedLogicalCreateEvent.imageCreateAction,
            representativeCreateAction: logicalCreateEvent.representativeCreateAction,
        };
    }, null);
};

const sendElementCreateMilestoneEvent = ({
    eventType,
    eventProperties,
    targetingConsentAllowed,
    userProperties,
}: {
    eventType: string;
    eventProperties?: Record<string, unknown>;
    targetingConsentAllowed: boolean;
    userProperties?: Record<string, unknown>;
}) => {
    sendAmplitudeEvent({
        eventType,
        eventProperties: {
            ...eventProperties,
            [AMPLITUDE_USER_PROPS.CONSENT_TARGETING_COOKIES]: targetingConsentAllowed,
        },
        userProperties,
    });
};

const getPendingEvent = (
    trackerType: UserAnalyticsTrackerType,
    logicalCreateEvent: LogicalCreateEvent,
    userDetails: Record<string, unknown>,
): PendingEvent => {
    const isFirstImageMilestone = trackerType === UserAnalyticsTrackerType.FIRST_IMAGE_CREATION;
    const sourceAction = isFirstImageMilestone
        ? logicalCreateEvent.imageCreateAction || logicalCreateEvent.representativeCreateAction
        : logicalCreateEvent.representativeCreateAction;
    const amplitudeEventProperties = getAmplitudeEventData(sourceAction, undefined, userDetails);

    return {
        eventProperties: amplitudeEventProperties.event_properties,
        userProperties: amplitudeEventProperties.user_properties,
    };
};

/**
 * Determines whether an action should advance the persisted create milestones and which
 * milestone events should be fired from that logical create event.
 */
export const getElementCreateMilestoneTrackingUpdate = ({
    action,
    userDetails = {},
    userAnalytics,
}: {
    action?: CreateAction;
    userDetails?: Record<string, unknown>;
    userAnalytics: MNUserAnalytics | undefined;
}): ElementCreateMilestoneTrackingUpdate | null => {
    if (!userAnalytics || !Object.keys(userAnalytics).length) return null;

    const logicalCreateEvent = getLogicalCreateEvent(action);
    if (!logicalCreateEvent) return null;

    const firstElementTracker = userAnalytics[UserAnalyticsTrackerType.FIRST_ELEMENT_CREATION];
    const thirdElementTracker = userAnalytics[UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION];
    const firstImageTracker = userAnalytics[UserAnalyticsTrackerType.FIRST_IMAGE_CREATION];

    const isFirstElementMilestonePending = isPendingTracker(firstElementTracker);
    const isThirdElementMilestonePending = isPendingTracker(thirdElementTracker);
    const isFirstImageMilestonePending = isPendingTracker(firstImageTracker);

    const currentCount = thirdElementTracker?.state?.elementCreationCount || 0;
    const nextElementCreationCount = isThirdElementMilestonePending ? currentCount + 1 : currentCount;

    const completedTrackerTypes: UserAnalyticsTrackerType[] = [];
    let updatedTracker: ElementCreateMilestoneTrackingUpdate['updatedTracker'];

    if (isFirstElementMilestonePending) {
        completedTrackerTypes.push(UserAnalyticsTrackerType.FIRST_ELEMENT_CREATION);
    }

    if (isThirdElementMilestonePending) {
        if (nextElementCreationCount >= 3) {
            completedTrackerTypes.push(UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION);
        } else {
            updatedTracker = {
                trackerType: UserAnalyticsTrackerType.THIRD_ELEMENT_CREATION,
                state: { elementCreationCount: nextElementCreationCount },
            };
        }
    }

    if (isFirstImageMilestonePending && logicalCreateEvent.containsImage) {
        completedTrackerTypes.push(UserAnalyticsTrackerType.FIRST_IMAGE_CREATION);
    }

    if (!completedTrackerTypes.length && !updatedTracker) return null;

    return {
        completedTrackers: completedTrackerTypes.map((trackerType) => ({
            trackerType,
            pendingEvent: getPendingEvent(trackerType, logicalCreateEvent, userDetails),
        })),
        updatedTracker,
    };
};

export const trackElementCreateMilestones = (
    store: ReduxStore,
    action: CreateAction,
    userDetails: Record<string, unknown>,
    userAnalytics: MNUserAnalytics | undefined,
) => {
    const milestoneUpdate = getElementCreateMilestoneTrackingUpdate({ action, userAnalytics, userDetails });

    if (!milestoneUpdate) return;

    const { completedTrackers, updatedTracker } = milestoneUpdate;
    const targetingConsentAllowed = hasOneTrustTargetingConsent();

    completedTrackers.forEach(({ pendingEvent, trackerType }) => {
        const eventType = EVENT_TYPE_BY_TRACKER_TYPE[trackerType];

        if (targetingConsentAllowed) {
            sendElementCreateMilestoneEvent({ eventType, targetingConsentAllowed, ...pendingEvent });
            store.dispatch(removeUserAnalyticsTracker({ trackerType }));
            return;
        }

        store.dispatch(
            updateUserAnalyticsTracker({
                trackerType,
                pendingEvent,
            }),
        );
    });

    if (updatedTracker) {
        // Some milestone actions only advance pending state, like incrementing the third element count.
        store.dispatch(updateUserAnalyticsTracker(updatedTracker));
    }
};

/**
 * Sends completed element-create milestones once targeting consent is available.
 */
export const sendCompletedMilestoneEventsThunk = (): GenericReduxThunkAction<void> => (dispatch, getState) => {
    const targetingConsentAllowed = hasOneTrustTargetingConsent();
    if (!targetingConsentAllowed) return;

    const analytics = getUserAnalytics(getState()) || {};

    ELEMENT_CREATE_MILESTONE_TRACKER_TYPES.forEach((trackerType) => {
        const tracker = analytics[trackerType];
        if (!tracker?.pendingEvent) return;

        sendElementCreateMilestoneEvent({
            eventType: EVENT_TYPE_BY_TRACKER_TYPE[trackerType],
            eventProperties: tracker.pendingEvent.eventProperties,
            targetingConsentAllowed,
            userProperties: tracker.pendingEvent.userProperties,
        });
        dispatch(removeUserAnalyticsTracker({ trackerType }));
    });
};
